import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback, useEffect, useRef, useState } from "react";

// Minimum loader duration so user can see that the recheck action was executed
const MIN_RECHECK_LOADER_DURATION_MS = 1000;

export type RecheckOutcome = "inside" | "outside" | "error";

interface ReturnForSignatureBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  workerId: string;
  isCheckingLocation: boolean;
  isShiftVerified: boolean;
  onRecheckLocation: () => Promise<RecheckOutcome>;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ReturnForSignatureBottomSheet(props: ReturnForSignatureBottomSheetProps) {
  const {
    modalState,
    shiftId,
    workerId,
    isCheckingLocation,
    isShiftVerified,
    onRecheckLocation,
    onCancel,
    onConfirm,
  } = props;

  const { showErrorToast } = useToast();
  const [isRecheckingWithMinDuration, setIsRecheckingWithMinDuration] = useState(false);
  const recheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (recheckTimerRef.current) {
        clearTimeout(recheckTimerRef.current);
      }
    };
  }, []);

  const handleRecheckLocation = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.MPR_RECHECK_LOCATION_CLICKED, {
      shiftId,
      workerId,
    });

    const startTime = Date.now();
    setIsRecheckingWithMinDuration(true);

    const waitForMinDuration = async () => {
      const timeElapsedInMs = Date.now() - startTime;
      if (timeElapsedInMs < MIN_RECHECK_LOADER_DURATION_MS) {
        await new Promise<void>((resolve) => {
          recheckTimerRef.current = setTimeout(() => {
            recheckTimerRef.current = null;
            resolve();
          }, MIN_RECHECK_LOADER_DURATION_MS - timeElapsedInMs);
        });
      }
    };

    try {
      const outcome = await onRecheckLocation();

      await waitForMinDuration();

      if (outcome === "outside") {
        showErrorToast(
          "It looks like you're away from the workplace. Please return to the workplace and try again."
        );
      } else if (outcome === "error") {
        showErrorToast("We couldn't verify your location. Please try again.");
      }
    } catch {
      await waitForMinDuration();
      showErrorToast("We couldn't verify your location. Please try again.");
    } finally {
      setIsRecheckingWithMinDuration(false);
    }
  }, [onRecheckLocation, showErrorToast, shiftId, workerId]);

  const isLoading = isCheckingLocation || isRecheckingWithMinDuration;

  const confirmWithoutSignatureModalState = useModalState();

  const handleContinueWithoutSignature = useCallback(() => {
    modalState.closeModal();
    confirmWithoutSignatureModalState.openModal();
  }, [modalState, confirmWithoutSignatureModalState]);

  const handleCancelConfirmation = useCallback(() => {
    confirmWithoutSignatureModalState.closeModal();
    onCancel();
  }, [confirmWithoutSignatureModalState, onCancel]);

  const handleConfirm = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.MPR_CONFIRM_WITHOUT_SIGNATURE_CLICKED, {
      shiftId,
      workerId,
    });
    confirmWithoutSignatureModalState.closeModal();
    onConfirm();
  }, [confirmWithoutSignatureModalState, onConfirm, shiftId, workerId]);

  return (
    <>
      <BottomSheet
        modalState={modalState}
        canDismiss={!isLoading}
        header={<BottomSheetHeader onClose={onCancel} />}
        onClose={onCancel}
      >
        <BottomSheetIllustrationContent
          illustrationType="facility"
          title="Return for a signature"
          description="It looks like you've left the workplace. To avoid approval delays, timesheets should be reviewed and signed by a workplace supervisor whenever possible."
        />
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isLoading}
            onClick={onCancel}
          >
            I will return to the workplace
          </LoadingButton>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={isLoading}
            onClick={handleContinueWithoutSignature}
          >
            Continue without signature
          </Button>
          <TextButton size="large" disabled={isLoading} onClick={handleRecheckLocation}>
            I&apos;m still at the workplace
          </TextButton>
        </DialogFooter>
      </BottomSheet>

      {/* Confirmation Bottom Sheet */}
      <BottomSheet
        modalState={confirmWithoutSignatureModalState}
        header={<BottomSheetHeader onClose={handleCancelConfirmation} />}
        onClose={handleCancelConfirmation}
      >
        <Stack spacing={4} sx={{ paddingX: 5 }}>
          <Title component="h2" sx={{ textWrap: "balance", textAlign: "center" }}>
            Are you sure?
          </Title>
          <Text
            semibold
            variant="body1"
            sx={{ textAlign: "center", color: (theme) => theme.palette.text.secondary }}
          >
            {isShiftVerified
              ? "Requests for shift adjustment are more likely to be rejected without a signature from a workplace employee."
              : "Requests for shift payment are more likely to be rejected without a signature from a workplace employee."}
          </Text>
        </Stack>
        <DialogFooter orientation="vertical" sx={{ marginTop: 10 }}>
          <Button fullWidth variant="contained" size="large" onClick={handleCancelConfirmation}>
            I will return to the workplace
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleConfirm}>
            Continue without signature
          </Button>
        </DialogFooter>
      </BottomSheet>
    </>
  );
}
